import React, {
  createContext, useState, useEffect, useMemo,
} from "react";
import axios from "axios";
import Cookies from "js-cookie";
import AxiosService from "services/Axios";
import { toast } from "react-toastify";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  const clearAuthState = () => {
    Cookies.remove("token");
    setUser(null);
    setIsUserLoggedIn(false);
  };

  const setToken = async (token) => {
    Cookies.set("token", token, {
      expires: 7,
      sameSite: "Strict",
      secure: window.location.protocol === "https:",
    });
    setIsUserLoggedIn(true);
  };

  const getProfile = async () => {
    try {
      const res = await AxiosService.get(`${process.env.REACT_APP_API_URL}/user/profile`);
      setUser(res.data.responseObject);
      setIsUserLoggedIn(true);
      return res.data.responseObject;
    } catch (error) {
      clearAuthState();
      throw error;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      const token = Cookies.get("token");

      if (!token) {
        if (isMounted) {
          setLoading(false);
        }
        return;
      }

      try {
        await getProfile();
      } catch (error) {
        toast.error("유효한 토큰이 아닙니다.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const signIn = async (email, password, redirectPath = "/test") => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/user/signin`, { email, password });
      const { token } = response.data.responseObject;
      await setToken(token);
      await getProfile();
      if (redirectPath) {
        window.location.href = redirectPath;
      }
    } catch (error) {
      // toast.error('Sign-in failed:', error);
      throw error;
    }
  };

  const signUp = async (formData) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/user/signup`, formData);
      await signIn(formData.email, formData.password, null);
      window.location.href = "/account?redirect=start";
    } catch (error) {
      // toast.error('Sign-up failed:', error);
      throw error;
    }
  };

  const updateProfile = async (formData) => {
    try {
      await AxiosService.post(`${process.env.REACT_APP_API_URL}/user/profile`, formData);
      await getProfile();
    } catch (error) {
      // toast.error('Update profile failed:', error);
      throw error;
    }
  };

  const updateFeedback = async (formData) => {
    try {
      await AxiosService.post(`${process.env.REACT_APP_API_URL}/user/feedback`, formData);
    } catch (error) {
      // toast.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const getFeedback = async (testType) => {
    try {
      const res = await AxiosService.get(`${process.env.REACT_APP_API_URL}/user/feedback/${testType}`);
      return res.data.responseObject;
    } catch (error) {
      // toast.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const updatePremiumReportFeedback = async (formData, reportId) => {
    try {
      await AxiosService.post(`${process.env.REACT_APP_API_URL}/user/feedback/report/${reportId}`, { ...formData, reportId });
    } catch (error) {
      // toast.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const getPremiumReportFeedback = async (reportId) => {
    try {
      const res = await AxiosService.get(`${process.env.REACT_APP_API_URL}/user/feedback/report/${reportId}`);
      return res.data.responseObject;
    } catch (error) {
      // toast.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const deleteAccount = async () => {
    try {
      const res = await AxiosService.delete(`${process.env.REACT_APP_API_URL}/user`);
      await signOut();
      return res.data.responseObject;
    } catch (error) {
      throw error;
    }
  };

  const signOut = (redirectPath = "/signin") => {
    clearAuthState();
    window.location.href = redirectPath;
  };

  const isLoggedIn = () => isUserLoggedIn;

  const value = useMemo(
    () => ({
      signIn,
      signUp,
      signOut,
      user,
      isLoggedIn,
      updateProfile,
      getProfile,
      updateFeedback,
      getFeedback,
      setToken,
      deleteAccount,
      updatePremiumReportFeedback,
      getPremiumReportFeedback,
    }),
    [
      signIn,
      signUp,
      signOut,
      user,
      isLoggedIn,
      updateProfile,
      getProfile,
      updateFeedback,
      getFeedback,
      setToken,
      deleteAccount,
      updatePremiumReportFeedback,
      getPremiumReportFeedback,
    ],
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
